/**
 * ブランド詳細ページの「トーン＆マナー」(B 群・docs/features/brand-product-restructure-plan.md §2.1 / §2.2 / §2.3)。
 *
 * ブランドの見た目 (配色・書体・面の様式・雰囲気) の正本 `brands.design_language` を 1 つの編集 UI で扱う。
 * 旧「ブランドカラー」(3 色) と「ブランドフォント」のカードはここに統合され、保存は design_language を送るだけ
 * (backend の brand_visual が旧列 brand_main/sub/accent_color・brand_font_families を同期する)。
 * - 配色: role 付きのパレット。メイン / サブ / アクセントが旧 3 色に対応する (同じ role は先頭が採られる)
 * - 書体: 「利用できる書体」(typography.brandFonts・配信済みカタログに限定・テロップと静止画の書体参考) と、
 *   サイトから読み取った参考書体 (display / body・任意の文字列・モーション向け)
 * - 「AIでブランド情報を再生成」(旧「サイトから提案を受ける」・§2.3.1): 有料の非同期ジョブ (brand_site_proposal)。欄にはボタンだけを置き、
 *   押したらモーダルで読み取る URL を入れさせる (既定は保存済みの読み取り元 > 登録 URL の先頭・登録 URL は入力候補。利用者指示 2026-10-30)。
 *   縦ステッパーで段階を見せ、完了したら
 *   トーン＆マナーをフォームに入れる (利用できる書体は引き継ぐ)・名前 / タグライン / 説明は現在値との差分で見せて
 *   項目ごとに適用・ロゴ候補は「ロゴとして登録」で登録する。提案は保存されない (利用者が確認して保存 / 適用)
 * - 保存 (利用者指示 2026-10-30): 手で直す操作は項目ごとにその場で保存する。選択・追加・削除・✓ での決定は即時、
 *   文字の入力欄は欄から離れたとき、色の選択ダイアログは操作が止まってから。失敗したら保存済みの値に戻して知らせる。
 *   AI の再生成の結果だけは配色・書体などをまとめて差し替えるので下書きにし、「保存」/「変更を取り消す」で確定する
 * 利用者向けの呼称は「トーン＆マナー」(「デザイン言語」は内部名のみ)。
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Activity, Blend, CaseSensitive, FileText, Layers, Paintbrush, Palette, Plus, Sparkles, Type, X, type LucideIcon } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { HelpTooltip } from '../../components/common/HelpTooltip';
import { GenerateButton } from '../../components/generation/common/GenerateButton';
import { JobStepper } from '../../components/common/JobStepper';
import { isValidBrandUrl, useBrandUrls } from '../../features/brands/urls';
import {
  DESIGN_MOOD_MAX,
  DESIGN_MOTION_HINTS_MAX,
  DESIGN_PALETTE_ROLES,
  DESIGN_RADII,
  DESIGN_SUMMARY_MAX,
  DESIGN_SURFACE_STYLES,
  cleanDesignLanguageForSave,
  emptyDesignLanguage,
  normalizeDesignLanguage,
  type BrandDesignLanguage,
  type DesignFont,
  type DesignPaletteRole,
  type DesignRadius,
  type DesignSurfaceStyle,
} from '../../features/brands/designLanguage';
import {
  SITE_PROPOSAL_STAGE_LABELS,
  buildIdentityItems,
  officialNotice,
  proposedDesignLanguage,
  registerProposedLogo,
  siteProposalStages,
  type IdentityField,
  type IdentityItem,
  type SiteProposalLogo,
} from '../../features/brands/siteProposal';
import { useBrandSiteProposal } from '../../features/brands/useBrandSiteProposal';
import { BRAND_FONT_HELP, BrandFontField } from './BrandFontField';
import { SingleSelectDropdown } from '../../components/common/SingleSelectDropdown';
import { InlineEditText } from './InlineEditText';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

interface Props {
  brandId: string;
  /** 保存済みのトーン＆マナー (brand.design_language) */
  designLanguage: unknown;
  /** 名前 / タグライン / 説明の現在値 (提案との差分に使う) */
  identity: { brand_name?: string | null; tagline?: string | null; description?: string | null };
  /** ブランドの更新 (design_language を載せる)。失敗は reject */
  onSave: (designLanguage: BrandDesignLanguage | null) => Promise<unknown>;
  /** 名前 / タグライン / 説明の提案を適用する (ブランドの更新)。失敗は reject */
  onApplyIdentity: (patch: Partial<Record<IdentityField, string>>) => Promise<unknown>;
  /** ロゴ候補を登録した後 (ロゴ一覧の取り直し用) */
  onLogoRegistered?: () => void;
  /** 未保存の変更の有無が変わったら親へ知らせる (親の離脱警告 useBlocker / beforeunload に含めるため) */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * 親 (ブランド詳細の見出し帯) の「AIでブランド情報を再生成」が押された回数。増えたら URL のモーダルを開く。
   * 渡されたときはこの欄の中の再生成ボタンを出さない (ボタンは親が持つ)
   */
  regenerateRequest?: number;
  /** 再生成の実行中かどうかを親へ (親のボタンの文言・無効化用) */
  onRegenerateRunningChange?: (running: boolean) => void;
}

function errorMessage(err: unknown, fallback: string): string {
  const resp = (err as { response?: { status?: number; data?: { error?: string } } })?.response;
  if (resp?.data?.error) return resp.data.error;
  return err instanceof Error && err.message ? err.message : fallback;
}

/** 項目の見出し (h2 + lucide アイコン・ブランド詳細のほかのタブのカード見出しと同じ格。2026-10-30 に h3 から昇格)。補足は細字で添え、help があれば右にヘルプのツールチップを出す */
function FieldHeading({ icon: Icon, children, note, help }: { icon: LucideIcon; children: ReactNode; note?: ReactNode; help?: ReactNode }) {
  return (
    <h2 className="design-language-heading">
      <Icon size={18} aria-hidden="true" />
      <span>{children}</span>
      {note && <span className="design-language-heading-note">{note}</span>}
      {help && <HelpTooltip ariaLabel={`${children}のヘルプ`}>{help}</HelpTooltip>}
    </h2>
  );
}

/** 既定で見せる配色の数。それ以上は「残り N 色を表示」で開く (利用者指示 2026-10-30) */
const PALETTE_PREVIEW_COUNT = 3;

/** 文字列のチップ (追加・削除)。 */
function ChipsField({
  label, values, max, placeholder, onChange, testId, icon, className = '',
}: {
  label: string;
  icon: LucideIcon;
  values: string[];
  max: number;
  placeholder: string;
  onChange: (next: string[]) => void;
  testId: string;
  className?: string;
}) {
  const [draft, setDraft] = useState('');
  // 入力欄は「追加」のピルを押したときだけ出す (利用者指示 2026-10-30)
  const [adding, setAdding] = useState(false);
  const add = () => {
    const v = draft.trim();
    if (!v || values.includes(v) || values.length >= max) return;
    onChange([...values, v]);
    setDraft('');
  };
  return (
    <div className={`design-language-field ${className}`} data-testid={testId}>
      <FieldHeading icon={icon} note={`(${values.length} / ${max})`}>{label}</FieldHeading>
      <div className="design-language-chips">
        {values.map((v) => (
          <span key={v} className="design-language-chip">
            {v}
            <button type="button" aria-label={`${v} を削除`} onClick={() => onChange(values.filter((x) => x !== v))}>
              <X size={12} />
            </button>
          </span>
        ))}
        {values.length < max && (adding ? (
          <input
            className="form-input design-language-chip-input"
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing) return;
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
              if (e.key === 'Escape') { setDraft(''); setAdding(false); }
            }}
            // 入力欄から離れたら入れた分を追加して閉じる (続けて入れるときは Enter)
            onBlur={() => { add(); setAdding(false); }}
            aria-label={`${label}を追加`}
            autoFocus
          />
        ) : (
          <button type="button" className="design-language-chip-add" onClick={() => setAdding(true)} aria-label={`${label}を追加`}>
            <Plus size={12} aria-hidden="true" />
            追加
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * 参考書体の 1 行 (見出し / 本文)。値が無いときは行を出さず「＋ {label}の書体」のピルだけ出し、押したら書体名の欄を開く
 * (配信カタログ (利用できる書体) に無いブランドの書体を手で書いておく欄・利用者指示 2026-10-30)
 */
function ReferenceFontSlot({ label, value, onChange }: { label: string; value?: DesignFont; onChange: (next?: DesignFont) => void }) {
  const [adding, setAdding] = useState(false);
  if (value?.family || adding) {
    return (
      <FontRow
        label={label}
        value={value}
        startEditing={!value?.family}
        onCancel={() => setAdding(false)}
        onChange={(next) => { setAdding(false); onChange(next); }}
      />
    );
  }
  return (
    <button type="button" className="design-language-chip-add" onClick={() => setAdding(true)} aria-label={`${label}の書体を追加`}>
      <Plus size={12} aria-hidden="true" />
      {label}の書体
    </button>
  );
}

function FontRow({ label, value, onChange, startEditing, onCancel }: { label: string; value?: DesignFont; onChange: (next?: DesignFont) => void; startEditing?: boolean; onCancel?: () => void }) {
  return (
    <div className="design-language-font-row">
      <span className="design-language-label">{label}</span>
      <InlineEditText
        className="design-language-font-family"
        value={value?.family ?? ''}
        placeholder={label === '見出し' ? '書体名 (例: 新ゴ / Helvetica Neue)' : '書体名 (例: リュウミン / Avenir)'}
        emptyText="書体名 (未設定)"
        ariaLabel={`${label}の書体名`}
        defaultEditing={startEditing}
        onCancel={onCancel}
        onCommit={(family) => onChange({ ...(value ?? {}), family })}
      />
      <InlineEditText
        className="design-language-font-weight"
        value={value?.weight != null ? String(value.weight) : ''}
        placeholder="太さ (例: 700)"
        emptyText="太さ (未設定)"
        ariaLabel={`${label}の太さ`}
        onCommit={(raw) => {
          const n = Number(raw);
          onChange({ ...(value ?? { family: '' }), weight: raw === '' ? undefined : (Number.isFinite(n) && /^\d+$/.test(raw) ? n : raw) });
        }}
      />
    </div>
  );
}

/** 提案の名前 / タグライン / 説明を現在値との差分で見せ、項目ごとに適用する。 */
function IdentityProposals({ items, onApply, busy }: { items: IdentityItem[]; onApply: (patch: Partial<Record<IdentityField, string>>) => Promise<void>; busy: boolean }) {
  const [adopted, setAdopted] = useState<Set<IdentityField>>(() => new Set(items.filter((i) => i.adopt).map((i) => i.key)));
  useEffect(() => { setAdopted(new Set(items.filter((i) => i.adopt).map((i) => i.key))); }, [items]);
  if (items.length === 0) return null;
  const apply = () => {
    const patch: Partial<Record<IdentityField, string>> = {};
    for (const item of items) if (adopted.has(item.key)) patch[item.key] = item.proposed;
    void onApply(patch);
  };
  return (
    <div className="design-language-field" data-testid="tone-manner-identity">
      <span className="design-language-label">名前・タグライン・説明の提案 (チェックした項目をブランドに適用します)</span>
      <ul className="tone-manner-proposal-list">
        {items.map((item) => (
          <li key={item.key} className="tone-manner-proposal-row">
            <label className="form-checkbox-label form-checkbox-label--compact">
              <input
                type="checkbox"
                checked={adopted.has(item.key)}
                onChange={(e) => setAdopted((prev) => { const next = new Set(prev); if (e.target.checked) next.add(item.key); else next.delete(item.key); return next; })}
                disabled={busy}
              />
              <span>{item.label}</span>
            </label>
            <div className="tone-manner-proposal-values">
              <span className="tone-manner-proposal-current">{item.current || '（未設定）'}</span>
              <span className="tone-manner-proposal-arrow" aria-hidden="true">→</span>
              <span className="tone-manner-proposal-proposed">{item.proposed}</span>
            </div>
          </li>
        ))}
      </ul>
      <div className="create-form-actions">
        <button type="button" className="btn-modal-sub" onClick={apply} disabled={busy || adopted.size === 0} data-testid="tone-manner-identity-apply">
          チェックした項目を適用
        </button>
      </div>
    </div>
  );
}

export function BrandToneMannerSection({
  brandId, designLanguage, identity, onSave, onApplyIdentity, onLogoRegistered, onDirtyChange, regenerateRequest, onRegenerateRunningChange,
}: Props) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: urlsData } = useBrandUrls(brandId);
  const urls = useMemo(() => urlsData ?? [], [urlsData]);
  const saved = useMemo(() => normalizeDesignLanguage(designLanguage), [designLanguage]);

  const [form, setForm] = useState<BrandDesignLanguage>(() => saved ?? emptyDesignLanguage());
  // AI の再生成の結果を入れた未保存の下書き (「保存」/「変更を取り消す」で確定)。手の編集は項目ごとに即時保存する
  const [draft, setDraft] = useState(false);
  // 最新のフォーム (連続した操作でも古い値に重ねないため)・最後に保存した (保存中を含む) 値・入力途中で未保存の変更
  const formRef = useRef(form);
  const lastSavedRef = useRef<BrandDesignLanguage>(saved ?? emptyDesignLanguage());
  const localEditRef = useRef(false);
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const pendingSavesRef = useRef(0);
  const colorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  // 読み取る URL を入れるモーダル (null = 閉じている)
  const [urlDialog, setUrlDialog] = useState<{ url: string; error: string | null } | null>(null);
  const [saving, setSaving] = useState(false);
  // 配色は既定で 3 色まで。残りは展開で見せる
  const [paletteExpanded, setPaletteExpanded] = useState(false);

  // サイトからの提案 (ジョブ)。投入・進捗・結果は共通フック
  const proposal = useBrandSiteProposal();
  const { phase, progress, result, elapsed, creditsPerRun, creditBadge, error: proposalError } = proposal;
  const [screenshotBroken, setScreenshotBroken] = useState(false);
  const [registering, setRegistering] = useState<number | null>(null);
  const [registered, setRegistered] = useState<Set<number>>(new Set());
  const [applyingIdentity, setApplyingIdentity] = useState(false);

  const setFormBoth = (next: BrandDesignLanguage) => {
    formRef.current = next;
    setForm(next);
  };

  // 保存済みの値が変わったら (保存後の再取得など)、下書きでも入力途中でも保存中でもなければフォームを揃える
  useEffect(() => {
    if (pendingSavesRef.current > 0) return;
    const base = saved ?? emptyDesignLanguage();
    lastSavedRef.current = base;
    if (!draft && !localEditRef.current) setFormBoth(base);
  }, [saved, draft]);

  // 未保存の変更 (AI の下書き) の有無を親へ (保存・取り消しで false・アンマウント時も false)
  useEffect(() => {
    onDirtyChange?.(draft);
  }, [draft, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  useEffect(() => () => {
    if (colorTimerRef.current) clearTimeout(colorTimerRef.current);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  /** 保存する (前の保存の完了を待って順に送る)。保存済みと同じなら送らない。失敗したら保存済みの値に戻す */
  const persist = (next: BrandDesignLanguage) => {
    const cleaned = cleanDesignLanguageForSave(next);
    if (JSON.stringify(cleaned) === JSON.stringify(cleanDesignLanguageForSave(lastSavedRef.current))) return;
    const before = lastSavedRef.current;
    lastSavedRef.current = next;
    pendingSavesRef.current += 1;
    setSaveState('saving');
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    saveChainRef.current = saveChainRef.current
      .then(() => onSave(cleaned))
      .then(
        () => {
          if (pendingSavesRef.current > 1) return;
          setSaveState('saved');
          savedTimerRef.current = setTimeout(() => setSaveState('idle'), 2000);
        },
        (err) => {
          lastSavedRef.current = before;
          localEditRef.current = false;
          setFormBoth(before);
          setSaveState('idle');
          toast.error(errorMessage(err, 'トーン＆マナーの保存に失敗しました'));
        },
      )
      .finally(() => { pendingSavesRef.current -= 1; });
  };

  /** 入力途中の変更 (色の選択ダイアログ)。保存は flush (操作が止まったとき・離れたとき) で。文字の欄は InlineEditText の決定で commit する */
  const change = (patch: Partial<BrandDesignLanguage>) => {
    setFormBoth({ ...formRef.current, ...patch });
    if (!draft) localEditRef.current = true;
  };

  /** 確定した変更 (選択・追加・削除・✓)。下書きでなければその場で保存する */
  const commit = (patch: Partial<BrandDesignLanguage>) => {
    const next = { ...formRef.current, ...patch };
    setFormBoth(next);
    if (draft) return;
    localEditRef.current = false;
    persist(next);
  };

  /** 入力途中の変更を保存する (色の選択の一段落・blur・AI の再生成の前) */
  const flush = () => {
    if (colorTimerRef.current) { clearTimeout(colorTimerRef.current); colorTimerRef.current = null; }
    if (draft || !localEditRef.current) return;
    localEditRef.current = false;
    persist(formRef.current);
  };

  /** 色の選択ダイアログはドラッグ中も onChange が続くので、止まってから保存する */
  const changeColor = (index: number, hex: string) => {
    change({ palette: formRef.current.palette.map((x, j) => (j === index ? { ...x, hex } : x)) });
    if (draft) return;
    if (colorTimerRef.current) clearTimeout(colorTimerRef.current);
    colorTimerRef.current = setTimeout(flush, 600);
  };

  /** パレットの 1 色を書き換えて確定する */
  const commitPaletteItem = (index: number, patch: Partial<BrandDesignLanguage['palette'][number]>) => {
    commit({ palette: formRef.current.palette.map((x, j) => (j === index ? { ...x, ...patch } : x)) });
  };
  // モーダルの URL の既定値: 保存済みの読み取り元 > 登録 URL の先頭 > 空
  const defaultUrl = saved?.sourceUrl ?? urls[0]?.url ?? '';

  const openUrlDialog = () => {
    if (creditsPerRun === null) {
      toast.error('ブランド情報の生成の料金が未設定のため、現在ご利用いただけません');
      return;
    }
    setUrlDialog({ url: defaultUrl, error: null });
  };

  // 親の再生成ボタン: 押された回数が増えたらモーダルを開く (実行中は開かない)。初回マウント時の値では開かない
  const handledRequestRef = useRef(regenerateRequest ?? 0);
  useEffect(() => {
    if (regenerateRequest === undefined || regenerateRequest === handledRequestRef.current) return;
    handledRequestRef.current = regenerateRequest;
    if (phase !== 'running') openUrlDialog();
    // 押された回数の変化だけを契機にする (openUrlDialog / phase の変化では開かない)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenerateRequest]);
  useEffect(() => {
    onRegenerateRunningChange?.(phase === 'running');
  }, [phase, onRegenerateRunningChange]);

  const handlePropose = async () => {
    if (!urlDialog) return;
    const targetUrl = urlDialog.url.trim();
    if (!isValidBrandUrl(targetUrl)) {
      setUrlDialog({ ...urlDialog, error: 'http または https のURLを入力してください' });
      return;
    }
    setUrlDialog(null);
    flush();
    setRegistered(new Set());
    const data = await proposal.start(targetUrl, brandId);
    if (!data) return;
    // 利用できる書体 (brandFonts) は提案に無いので、フォームの値を引き継ぐ (計画 §2.2.4)
    const dl = proposedDesignLanguage(data, form);
    if (dl) {
      localEditRef.current = false;
      setFormBoth({ ...dl, sourceUrl: dl.sourceUrl ?? targetUrl });
      setDraft(true);
    }
    setScreenshotBroken(false);
    toast.success(dl ? '提案を受け取りました。配色・書体を確認して「保存」してください' : '提案を受け取りました');
  };

  /** AI の下書きを保存する */
  const handleSave = async () => {
    setSaving(true);
    try {
      const next = formRef.current;
      await onSave(cleanDesignLanguageForSave(next));
      lastSavedRef.current = next;
      setDraft(false);
      toast.success('トーン＆マナーを保存しました');
    } catch (err) {
      toast.error(errorMessage(err, 'トーン＆マナーの保存に失敗しました'));
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setFormBoth(lastSavedRef.current);
    setDraft(false);
  };

  const identityItems = useMemo(() => buildIdentityItems(result?.identity ?? null, identity), [result, identity]);
  const applyIdentity = async (patch: Partial<Record<IdentityField, string>>) => {
    setApplyingIdentity(true);
    try {
      await onApplyIdentity(patch);
      toast.success('ブランドに適用しました');
    } catch (err) {
      toast.error(errorMessage(err, '適用に失敗しました'));
    } finally {
      setApplyingIdentity(false);
    }
  };

  /** ロゴ候補を登録 (既存のロゴ追加と同じ経路・registerProposedLogo) */
  const registerLogo = async (logo: SiteProposalLogo) => {
    if (!logo.signed_url) return;
    setRegistering(logo.index);
    try {
      await registerProposedLogo(brandId, logo);
      setRegistered((prev) => new Set(prev).add(logo.index));
      queryClient.invalidateQueries({ queryKey: ['logos'] });
      onLogoRegistered?.();
      toast.success('ロゴとして登録しました');
    } catch (err) {
      toast.error(errorMessage(err, 'ロゴの登録に失敗しました'));
    } finally {
      setRegistering(null);
    }
  };

  const busy = phase === 'running' || saving;
  const brandFonts = form.typography.brandFonts ?? [];
  const notice = result ? officialNotice(result.identity, result.source_url) : null;
  // ブランド詳細のタブの中 (再生成ボタンを親が持つ) かどうか
  const embedded = regenerateRequest !== undefined;

  return (
    <div className="card design-language-card" data-testid="brand-tone-manner">
      {/* ブランド詳細ではタブ名が見出しになるので、欄の見出しは出さず保存状態だけ右上に出す (利用者指示 2026-10-30) */}
      {embedded ? (
        saveState !== 'idle' && (
          <span className="design-language-save-state design-language-save-state--floating" role="status" data-testid="tone-manner-save-state">
            {saveState === 'saving' ? '保存中…' : '保存しました'}
          </span>
        )
      ) : (
      <h2 className="card-title" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <Palette size={18} aria-hidden="true" className="card-title-icon" style={{ marginRight: 0 }} />
        トーン＆マナー
        <HelpTooltip ariaLabel="トーン＆マナーのヘルプ">
          配色・書体・雰囲気など、ブランドらしさの決まりごとです。静止画・動画・モーショングラフィックスの生成で
          配色と書体の手本として使います。「AIでブランド情報を再生成」で、ブランドのサイトを読み取って AI に候補を作らせることもできます
          (有料・生成した内容は自動では保存されないので、確認して「保存」や「適用」をしてください)。
        </HelpTooltip>
        {/* 項目ごとの即時保存の状態 (失敗はトーストで知らせ、値は保存済みに戻す) */}
        {saveState !== 'idle' && (
          <span className="design-language-save-state" role="status" data-testid="tone-manner-save-state">
            {saveState === 'saving' ? '保存中…' : '保存しました'}
          </span>
        )}
      </h2>
      )}

      <div id="design-language-body" className="design-language-body">
      {/* AIでブランド情報を再生成 (旧「サイトから提案を受ける」)。URL は押してからモーダルで入れる */}
      {regenerateRequest === undefined && (
      <div className="design-language-extract-row">
        {/* ブランドは作成済みなので「再生成」。主役の初回生成 (新規作成モーダル) より一段控えめなモノクロ版 (利用者指示 2026-10-30)。
            ブランド詳細では見出し帯にボタンがあるので出さない (regenerateRequest を渡したとき) */}
        <GenerateButton
          className="btn-ai-director-md btn-ai-director-mono"
          label={phase === 'running' ? '生成しています…' : 'AIでブランド情報を再生成'}
          creditCost={creditBadge}
          onClick={openUrlDialog}
          disabled={busy || creditsPerRun === null}
          data-testid="tone-manner-propose"
        />
      </div>
      )}
      {phase === 'running' && (
        <JobStepper
          stages={siteProposalStages(progress)}
          labels={SITE_PROPOSAL_STAGE_LABELS}
          stepText={progress?.step?.text ?? null}
          elapsed={elapsed}
          ariaLabel="ブランド情報の生成の段階"
        />
      )}
      {phase === 'error' && proposalError && (
        <span className="design-language-unsaved" role="alert" data-testid="tone-manner-propose-error">{proposalError}</span>
      )}
      {phase === 'done' && result && (
        <div className="tone-manner-proposal" data-testid="tone-manner-proposal">
          {notice && <span className="inline-edit-hint tone-manner-official-notice" data-testid="tone-manner-official-notice">{notice}</span>}
          {result.notes.map((n) => <span key={n} className="inline-edit-hint">{n}</span>)}
          <IdentityProposals items={identityItems} onApply={applyIdentity} busy={applyingIdentity} />
          {result.logos.length > 0 && (
            <div className="design-language-field" data-testid="tone-manner-logos">
              <span className="design-language-label">ロゴの候補 (サイトの og:image・アイコンから。ブランドのロゴと断定したものではなく、低解像度のものは参考です)</span>
              <ul className="tone-manner-logo-list">
                {result.logos.map((logo) => (
                  <li key={logo.index} className="tone-manner-logo-item">
                    {logo.signed_url && <img src={logo.signed_url} alt={`ロゴ候補 ${logo.index + 1} (${logo.source})`} className="tone-manner-logo-image" />}
                    <span className="tone-manner-logo-meta">{logo.source}{logo.width && logo.height ? ` ${logo.width}×${logo.height}` : ''}</span>
                    {logo.registrable === false ? (
                      <span className="tone-manner-logo-meta" title="サイトから拾った低解像度の画像はクリエイティブ素材に向かないため登録できません">低解像度のため参考のみ</span>
                    ) : (
                      <button
                        type="button"
                        className="btn-modal-sm"
                        onClick={() => void registerLogo(logo)}
                        disabled={registering !== null || registered.has(logo.index) || !logo.signed_url}
                      >
                        {registered.has(logo.index) ? '登録済み' : registering === logo.index ? '登録中…' : 'ロゴとして登録'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.screenshot_url && !screenshotBroken && (
            <div className="design-language-field">
              <span className="design-language-label">読み取ったページ</span>
              <a href={result.screenshot_url} target="_blank" rel="noreferrer" className="design-language-screenshot">
                <img src={result.screenshot_url} alt="読み取り元サイトのスクリーンショット" onError={() => setScreenshotBroken(true)} />
              </a>
            </div>
          )}
        </div>
      )}
      {draft && <span className="design-language-unsaved" data-testid="tone-manner-unsaved">AIで再生成した内容は未保存です。確認して「保存」してください</span>}

      {/* 項目の並び (詳細項目の折りたたみは廃止・利用者指示 2026-10-30): 色 (ブランドカラー・グラデーション) は全幅、
         書体 / 雰囲気 (ムード・動きのヒント) / 面の様式・要約 は幅があれば 2 列 */}
      {/* ブランドカラー (palette。メイン / サブ / アクセントが旧ブランドカラー 3 色に対応。表示名は 2026-10-30 に「配色」から改名) */}
      <div className="design-language-field design-language-field--full">
        <FieldHeading
          icon={Paintbrush}
          help={
            <>
              <p style={{ margin: '0 0 6px 0' }}>ブランドで使う色 (ブランドカラー) と、その役割・使いどころです。</p>
              <p style={{ margin: '0 0 6px 0' }}>
                役割が「メイン」「サブ」「アクセント」の色は、静止画・動画の生成でブランドカラーとして使われます。同じ役割の色が複数あるときは、上にある色が使われます。
              </p>
              <p style={{ margin: '0 0 6px 0' }}>
                「背景」「面」「文字」を含むすべての色と使いどころは、モーショングラフィックス (ブランドカットなど) で配色の手本として使われます。使いどころは「見出しの帯」「ボタン」のように短く書くと伝わりやすくなります。
              </p>
              <p style={{ margin: 0 }}>変更はその場で保存されます (AIで再生成した内容だけは、確認して「保存」で確定します)。</p>
            </>
          }
        >
          ブランドカラー
        </FieldHeading>
        <div className="design-language-palette">
          {form.palette.map((p, i) => (paletteExpanded || i < PALETTE_PREVIEW_COUNT) && (
            <div key={i} className="design-language-swatch-row" data-testid={`tone-manner-swatch-${i}`}>
              <input
                type="color"
                className="brand-color-picker"
                value={HEX_RE.test(p.hex) ? p.hex : '#000000'}
                onChange={(e) => changeColor(i, e.target.value)}
                onBlur={flush}
                aria-label={`色 ${i + 1} を選択`}
              />
              <InlineEditText
                className="design-language-hex"
                textClassName="design-language-mono"
                value={p.hex}
                placeholder="#RRGGBB"
                ariaLabel={`色 ${i + 1} の hex`}
                onCommit={(hex) => {
                  if (!HEX_RE.test(hex)) {
                    toast.error('色は #RRGGBB の形で入力してください');
                    return false;
                  }
                  commitPaletteItem(i, { hex });
                }}
              />
              {/* ドロップダウンはサイト共通のカスタム (SingleSelectDropdown)。ここは枠なしの小さな形 (利用者指示 2026-10-30) */}
              <SingleSelectDropdown
                label={`色 ${i + 1} の役割`}
                labelOnly
                alwaysShowSelected
                fitContent
                unstyledTrigger
                triggerClassName="design-language-compact-trigger"
                className="design-language-role"
                value={p.role}
                onChange={(role) => commitPaletteItem(i, { role: role as DesignPaletteRole })}
                options={DESIGN_PALETTE_ROLES.map((r) => ({ value: r.value, label: r.label }))}
              />
              {/* 使いどころ: 平時は文字 + 鉛筆、鉛筆で編集 (ブランド基本情報の表と同じ操作・利用者指示 2026-10-30) */}
              <InlineEditText
                className="design-language-usage"
                value={p.usage ?? ''}
                placeholder="使いどころ (任意)"
                emptyText="使いどころ (未設定)"
                ariaLabel={`色 ${i + 1} の使いどころ`}
                onCommit={(usage) => commitPaletteItem(i, { usage: usage || undefined })}
              />
              <button
                type="button"
                className="brand-url-icon-button brand-url-icon-button--danger"
                aria-label={`色 ${i + 1} を削除`}
                onClick={() => commit({ palette: formRef.current.palette.filter((_, j) => j !== i) })}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          {form.palette.length > PALETTE_PREVIEW_COUNT && (
            <button
              type="button"
              className="design-language-palette-more"
              onClick={() => setPaletteExpanded((v) => !v)}
              aria-expanded={paletteExpanded}
              data-testid="tone-manner-palette-more"
            >
              {paletteExpanded ? '最初の3色だけ表示' : `残り${form.palette.length - PALETTE_PREVIEW_COUNT}色を表示`}
            </button>
          )}
          <button
            type="button"
            className="brand-add-button"
            onClick={() => { setPaletteExpanded(true); commit({ palette: [...formRef.current.palette, { hex: '#000000', role: formRef.current.palette.length === 0 ? 'primary' : 'accent' }] }); }}
          >
            <Plus size={14} aria-hidden="true" />
            カラーを追加
          </button>
        </div>
      </div>

      {/* グラデーション */}
      <div className="design-language-field design-language-field--full">
        <FieldHeading icon={Blend}>グラデーション</FieldHeading>
        <div className="design-language-palette">
          {form.gradients.map((g, i) => (
            <div key={i} className="design-language-swatch-row" data-testid={`tone-manner-gradient-${i}`}>
              <span className="design-language-gradient-preview" style={{ background: g.css }} aria-hidden="true" />
              <InlineEditText
                className="design-language-gradient-css"
                textClassName="design-language-mono"
                value={g.css}
                placeholder="linear-gradient(135deg, #ff7a00, #ff0069)"
                emptyText="CSS (未設定)"
                ariaLabel={`グラデーション ${i + 1} の CSS`}
                onCommit={(css) => commit({ gradients: formRef.current.gradients.map((x, j) => (j === i ? { ...x, css } : x)) })}
              />
              <InlineEditText
                className="design-language-usage"
                value={g.usage ?? ''}
                placeholder="使いどころ (任意)"
                emptyText="使いどころ (未設定)"
                ariaLabel={`グラデーション ${i + 1} の使いどころ`}
                onCommit={(usage) => commit({ gradients: formRef.current.gradients.map((x, j) => (j === i ? { ...x, usage: usage || undefined } : x)) })}
              />
              <button
                type="button"
                className="brand-url-icon-button brand-url-icon-button--danger"
                aria-label={`グラデーション ${i + 1} を削除`}
                onClick={() => commit({ gradients: formRef.current.gradients.filter((_, j) => j !== i) })}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="brand-add-button"
            onClick={() => commit({ gradients: [...formRef.current.gradients, { css: '' }] })}
          >
            <Plus size={14} aria-hidden="true" />
            グラデーションを追加
          </button>
        </div>
      </div>

      {/* 書体: 利用できる書体 (カタログ限定) + 参考書体 (サイトから読み取ったもの) */}
      <div className="design-language-field design-language-field--half" data-testid="tone-manner-fonts">
        {/* ヘルプは見出しの右 (ほかの項目と同じ位置・利用者指示 2026-10-30) */}
        <FieldHeading icon={Type} help={BRAND_FONT_HELP}>利用できる書体</FieldHeading>
        <BrandFontField
          value={brandFonts}
          onChange={(next) => commit({ typography: { ...formRef.current.typography, brandFonts: next } })}
          disabled={busy}
          showHelp={false}
        />
      </div>

      {/* 参考書体: 値のある行だけ出し、無い行は「＋ 見出しの書体」等のピルから足す (利用者指示 2026-10-30) */}
      <div className="design-language-field design-language-field--half">
        <FieldHeading
          icon={CaseSensitive}
          help={
            <>
              <p style={{ margin: '0 0 6px 0' }}>ブランドが使っている書体の名前を書いておく欄です。「利用できる書体」(配信中のフォント) に無い書体でも、名前をそのまま入れられます。</p>
              <p style={{ margin: '0 0 6px 0' }}>モーショングラフィックス (ブランドカットなど) で、文字の雰囲気を近い書体で再現する手本として使います。テロップや静止画で実際に使う書体は「利用できる書体」で選んでください。</p>
              <p style={{ margin: 0 }}>「AIでブランド情報を再生成」でサイトを読み取ると、AIが推定した書体が入ります。</p>
            </>
          }
        >
          参考書体
        </FieldHeading>
        <div className="design-language-reference-fonts">
          <ReferenceFontSlot
            label="見出し"
            value={form.typography.display}
            onChange={(display) => commit({ typography: { ...formRef.current.typography, display } })}
          />
          <ReferenceFontSlot
            label="本文"
            value={form.typography.body}
            onChange={(body) => commit({ typography: { ...formRef.current.typography, body } })}
          />
        </div>
      </div>

      <ChipsField
        label="ムード"
        icon={Sparkles}
        values={form.mood}
        max={DESIGN_MOOD_MAX}
        placeholder="例: 上品 (Enter で追加)"
        onChange={(mood) => commit({ mood })}
        className="design-language-field--half"
        testId="tone-manner-mood"
      />


      <ChipsField
        label="動きのヒント"
        icon={Activity}
        values={form.motionHints}
        max={DESIGN_MOTION_HINTS_MAX}
        placeholder="例: ゆっくりしたフェード (Enter で追加)"
        onChange={(motionHints) => commit({ motionHints })}
        className="design-language-field--half"
        testId="tone-manner-motion-hints"
      />


      {/* 面の様式 */}
      <div className="design-language-field design-language-field--half">
        <FieldHeading icon={Layers}>面の様式</FieldHeading>
        {/* 様式・角丸・影の 3 項目を 1 行ずつ (利用者指示 2026-10-30) */}
        <dl className="design-language-surface-rows">
          <div className="design-language-font-row">
            <dt className="design-language-label">様式</dt>
            <dd>
              <SingleSelectDropdown
                label="面の様式"
                labelOnly
                alwaysShowSelected
                fitContent
                unstyledTrigger
                triggerClassName="design-language-compact-trigger"
                value={form.surfaces.style ?? ''}
                onChange={(style) => commit({ surfaces: { ...formRef.current.surfaces, style: (style || undefined) as DesignSurfaceStyle | undefined } })}
                options={[{ value: '', label: '指定なし' }, ...DESIGN_SURFACE_STYLES.map((o) => ({ value: o.value, label: o.label }))]}
              />
            </dd>
          </div>
          <div className="design-language-font-row">
            <dt className="design-language-label">角丸</dt>
            <dd>
              <SingleSelectDropdown
                label="角丸"
                labelOnly
                alwaysShowSelected
                fitContent
                unstyledTrigger
                triggerClassName="design-language-compact-trigger"
                value={form.surfaces.radius ?? ''}
                onChange={(radius) => commit({ surfaces: { ...formRef.current.surfaces, radius: (radius || undefined) as DesignRadius | undefined } })}
                options={[{ value: '', label: '指定なし' }, ...DESIGN_RADII.map((o) => ({ value: o.value, label: o.label }))]}
              />
            </dd>
          </div>
          <div className="design-language-font-row">
            <dt className="design-language-label">影</dt>
            <dd>
              <InlineEditText
                value={form.surfaces.shadow ?? ''}
                placeholder="例: 柔らかい大きな影"
                ariaLabel="影"
                onCommit={(shadow) => commit({ surfaces: { ...formRef.current.surfaces, shadow: shadow || undefined } })}
              />
            </dd>
          </div>
        </dl>
      </div>

      <div className="design-language-field design-language-field--half">
        <FieldHeading icon={FileText} note={`(${form.summary.length} / ${DESIGN_SUMMARY_MAX} 字)`}>要約</FieldHeading>
        <InlineEditText
          multiline
          maxLength={DESIGN_SUMMARY_MAX}
          value={form.summary}
          placeholder="サイトの見た目の特徴を短くまとめます"
          emptyText="要約 (未設定)"
          ariaLabel="要約"
          onCommit={(summary) => commit({ summary: summary.slice(0, DESIGN_SUMMARY_MAX) })}
        />
      </div>

      {draft && (
        <div className="create-form-actions">
          <button type="button" className="btn-cancel" onClick={handleDiscard} disabled={busy}>変更を取り消す</button>
          <button type="button" className="btn-modal-sub" onClick={() => void handleSave()} disabled={busy} data-testid="tone-manner-save">
            {saving ? '保存中…' : '保存'}
          </button>
        </div>
      )}
      </div>
      {urlDialog && createPortal(
        // 入力の消失を防ぐため、オーバーレイのクリックでは閉じない (キャンセル / ✕ のみ)
        <div className="modal-overlay">
          <div className="modal-card modal-card--sm" role="dialog" aria-modal="true" aria-labelledby="tone-manner-url-dialog-title">
            <div className="modal-header">
              <h3 className="modal-title tone-manner-url-dialog-title" id="tone-manner-url-dialog-title">
                <Sparkles size={18} aria-hidden="true" />
                AIでブランド情報を再生成
              </h3>
              <button type="button" className="modal-close-button" onClick={() => setUrlDialog(null)} aria-label="閉じる">✕</button>
            </div>
            <div className="modal-body tone-manner-url-dialog-body">
              <p className="tone-manner-url-dialog-desc">
                ブランドURLを入れると、AIがウェブサイトの情報をもとに配色・書体・雰囲気・名前・説明・ロゴの候補を生成します。生成した内容は自動では保存されません。
              </p>
              <label className="tone-manner-url-dialog-label" htmlFor="tone-manner-url-input">ブランドURL</label>
              <input
                id="tone-manner-url-input"
                type="url"
                className="form-input"
                value={urlDialog.url}
                placeholder="https://example.com"
                list={urls.length > 0 ? 'tone-manner-url-options' : undefined}
                onChange={(e) => setUrlDialog({ url: e.target.value, error: null })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    if (urlDialog.url.trim()) void handlePropose();
                  }
                }}
                autoFocus
              />
              {/* 登録済みの URL は入力候補に出す (旧ドロップダウンの選択肢) */}
              {urls.length > 0 && (
                <datalist id="tone-manner-url-options">
                  {urls.map((u) => <option key={u.id} value={u.url}>{u.name ?? ''}</option>)}
                </datalist>
              )}
              {urlDialog.error && <span className="design-language-unsaved" role="alert">{urlDialog.error}</span>}
              <div className="modal-actions tone-manner-url-dialog-actions">
                <button type="button" className="btn-modal-secondary" onClick={() => setUrlDialog(null)}>キャンセル</button>
                <GenerateButton
                  className="btn-ai-director-md"
                  label="生成する"
                  creditCost={creditBadge}
                  onClick={() => void handlePropose()}
                  disabled={!urlDialog.url.trim()}
                  data-testid="tone-manner-propose-submit"
                />
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
